// taskOrderとparentTaskTemplateTaskOrderを持つオブジェクトの型定義
type TaskWithOrder = {
  taskOrder: string
  parentTaskTemplateTaskOrder: string | null
}

/**
 * タスクとその子孫のtaskOrderを再採番する関数
 *
 * この関数は、インデント/アウトデント等の階層をまたぐ操作の後に呼び出され、
 * タスクの階層構造に基づいてtaskOrderを再採番する
 *
 * taskOrderの採番ルール:
 * - ルートレベル: "1", "2", "3", ...
 * - 子タスク: "1-1", "1-2", "2-1", ...
 * - 孫タスク: "1-1-1", "1-1-2", ...
 *
 * - 親子関係は parentTaskTemplateTaskOrder を基準に判定する
 * - 再帰的に子孫すべてのtaskOrderを再採番する
 *
 * @param taskTemplateTasks すべてのタスクリスト
 * @param preserveOrder trueの場合、配列の順序を維持する（ドラッグアンドドロップ時に使用）
 * @returns 再採番されたタスクリストと、旧taskOrder -> 新taskOrder のマップ
 */
export const reassignTaskOrder = <T extends TaskWithOrder>(
  taskTemplateTasks: T[],
  preserveOrder: boolean = false
): {
  tasks: T[]
  orderMap: Map<string, string>
} => {
  const orderMap = new Map<string, string>() // 旧taskOrder -> 新taskOrder のマップ
  const result: T[] = []

  // 親taskOrder -> 子タスク配列 のマップを作成
  const childrenMap = new Map<string | null, T[]>()
  taskTemplateTasks.forEach((task) => {
    const parent = task.parentTaskTemplateTaskOrder
    if (!childrenMap.has(parent)) {
      childrenMap.set(parent, [])
    }

    // has()でチェック済みなので、pushする
    childrenMap.get(parent)?.push(task)
  })

  // preserveOrderがfalseの場合のみ、各グループをソート
  if (!preserveOrder) {
    childrenMap.forEach((children) => {
      children.sort((a, b) =>
        a.taskOrder.localeCompare(b.taskOrder, undefined, { numeric: true })
      )
    })
  }

  // ルートレベルのタスクから順に処理する再帰関数
  const processLevel = (parentOrder: string | null) => {
    const children = childrenMap.get(parentOrder) || []

    // 子タスクが存在しない場合は終了（再帰終わり）
    if (children.length === 0) {
      return
    }

    children.forEach((task, index) => {
      // 新しいtaskOrderを生成: ルートなら "1", "2", ... 子なら "1-1", "1-2", ...
      const newParentOrder = parentOrder
        ? orderMap.get(parentOrder) || parentOrder
        : null
      const newOrder = newParentOrder
        ? `${newParentOrder}-${index + 1}`
        : `${index + 1}`
      orderMap.set(task.taskOrder, newOrder)

      result.push({
        ...task,
        taskOrder: newOrder,
        parentTaskTemplateTaskOrder: newParentOrder,
      })

      // 再帰的に子タスクを処理（旧taskOrderを使って子を検索）
      processLevel(task.taskOrder)
    })
  }

  processLevel(null)
  return { tasks: result, orderMap }
}
